import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { coopInvestigationUrl } from '@/lib/moderationToolLinks';
import { cn } from '@/lib/utils';
import {
  isTipmiHidePending,
  isTipmiHoldPending,
  isTipmiPublishPending,
} from '@/features/moderation/lib/coopTipmiSync';
import { formatDurationMs } from '@/features/moderation/lib/formatDurationMs';
import { CoopScanDetails } from '@/features/moderation/components/CoopScanDetails';
import {
  formatCoopScanStatus,
  formatCoopSettledDetails,
  formatDeliveryLabel,
  isAwaitingCaseDelivery,
  normalizeDeliveryStatus,
  type CoopScanDetail,
} from '@/features/moderation/lib/coopDelivery';
import { useTickingNow } from '@/features/moderation/lib/useTickingNow';
import {
  formatCoopScanBadge,
  isDeepScanSettled,
  isProvisionalNoAction,
} from '@/features/moderation/lib/verdictDisplay';
import {
  rejectAttributionForScanLabel,
  type RejectAttribution,
} from '@/features/moderation/lib/pickRejectAttribution';
import type { ModerationIntelligence } from '@/features/moderation/types';
import {
  moderationStatusBadgeVariant,
  moderationStatusLabel,
  postPublicationHint,
  postStatusBadgeVariant,
  postStatusLabel,
  formatElapsedSince,
  scanProcessingStartedAt,
  normalizeModerationStatusKey,
  displayModerationStatus,
} from '../lib/contentDisplay';
import { pickCombinedRisk } from '@/features/moderation/lib/combinedRiskDisplay';
import { useContentIntelligence } from '../api';

function formatRejectBanner(attribution: RejectAttribution): string {
  const parts: string[] = ['Rejected'];
  if (attribution.ruleName) {
    parts.push(`by ${attribution.ruleName}`);
  }
  if (attribution.reason) {
    parts.push(`— ${attribution.reason}`);
  }
  if (attribution.policies.length > 0) {
    parts.push(`· Policies: ${attribution.policies.join(', ')}`);
  }
  return parts.join(' ');
}

const SCAN_STYLE = {
  pending: 'bg-muted text-muted-foreground',
  awaiting: 'bg-sky-100 text-sky-800 border-sky-200',
  approve: 'bg-green-100 text-green-800 border-green-200',
  reject: 'bg-red-100 text-red-800 border-red-200',
  escalate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  missing: 'bg-muted text-muted-foreground border-dashed',
} as const;

function scanBadgeClass(
  intelligence: ModerationIntelligence | null | undefined,
  provisional: boolean,
  missing: boolean,
  awaitingDelivery: boolean,
): string {
  // Opening style only for real outbox delivery (HOLD/LIVE_ENFORCE), not OBSERVE ledger-only PENDING.
  if (awaitingDelivery) return SCAN_STYLE.awaiting;
  if (missing) return SCAN_STYLE.missing;
  const caseStatus = (intelligence?.case_status ?? '').toLowerCase();
  if (caseStatus === 'failed' || caseStatus === 'timeout') return SCAN_STYLE.reject;
  if (normalizeDeliveryStatus(intelligence?.delivery_status) === 'failed') return SCAN_STYLE.reject;
  if (provisional) return SCAN_STYLE.awaiting;
  const upper = (intelligence?.verdict ?? '').toUpperCase();
  if (upper.includes('REJECT')) return SCAN_STYLE.reject;
  if (upper.includes('APPROVE') || upper.includes('BYPASS')) return SCAN_STYLE.approve;
  if (upper.includes('ESCALAT') || upper.includes('HUMAN')) return SCAN_STYLE.escalate;
  return SCAN_STYLE.pending;
}

function StatusCell({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 border-b border-border p-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="min-h-6">{children}</div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{hint}</p>
    </div>
  );
}

export function ContentModerationOverviewStrip({
  contentId,
  postStatus,
  moderationStatus,
  combinedRisk,
  riskTier,
  moderationLastError,
  moderationStartedAt,
  moderationDecidedAt,
}: {
  contentId: string;
  postStatus?: string | null;
  moderationStatus?: string | null;
  combinedRisk?: number | null;
  riskTier?: string | null;
  /** @deprecated Unused — TipMi ledger no longer surfaces Bedrock classify. */
  isAddCategory?: string | null;
  /** @deprecated Unused — TipMi ledger no longer surfaces Bedrock classify. */
  postProcessingError?: string | null;
  /** @deprecated Unused — ledger pending clock uses moderationStartedAt only. */
  bedrockLastAttemptAt?: string | null;
  moderationLastError?: string | null;
  moderationStartedAt?: string | null;
  moderationDecidedAt?: string | null;
  /** @deprecated Unused — ledger pending clock uses moderationStartedAt only. */
  createdAt?: string | null;
}) {
  const rawLedgerKey = normalizeModerationStatusKey(moderationStatus);
  const pollWhileMissing = rawLedgerKey === 'PENDING';
  const { data: intelligence, isLoading, isError } = useContentIntelligence(contentId, {
    pollWhileMissing,
  });
  const intelligenceRisk = pickCombinedRisk(intelligence ?? undefined);
  const status = displayModerationStatus(moderationStatus, {
    combinedRisk: combinedRisk ?? intelligenceRisk,
    riskTier,
  });
  const ledgerKey = normalizeModerationStatusKey(status);
  const publicationHint = postPublicationHint(postStatus);

  const awaitingDelivery = isAwaitingCaseDelivery(
    intelligence?.delivery_status,
    intelligence?.case_status,
  );
  const nowMs = useTickingNow(awaitingDelivery || pollWhileMissing);
  const missing =
    ((!isLoading && intelligence == null) || isError) && !awaitingDelivery;
  const ledgerPendingMissing = missing && pollWhileMissing;
  const deepScanSettled = isDeepScanSettled({
    deepScanDurationMs: intelligence?.deep_scan_duration_ms,
    deliveryStatus: intelligence?.delivery_status,
    vectors: intelligence?.vectors,
  });
  const provisional = isProvisionalNoAction(intelligence?.verdict, intelligence?.case_status, {
    deepScanSettled,
  });
  const hidePending = isTipmiHidePending(intelligence?.verdict, postStatus);
  const holdPending = isTipmiHoldPending(intelligence?.verdict, postStatus);
  const publishPending = isTipmiPublishPending(intelligence?.verdict, postStatus);

  const startedAt = scanProcessingStartedAt({
    moderationStartedAt,
  });
  // Elapsed/duration only for statuses we actually display (suppressed HUMAN_REVIEW → no clock).
  const elapsed = ledgerKey === 'PENDING' ? formatElapsedSince(startedAt) : null;
  const duration =
    !!ledgerKey && ledgerKey !== 'PENDING' && startedAt && moderationDecidedAt
      ? formatElapsedSince(startedAt, new Date(moderationDecidedAt).getTime())
      : null;
  // TipMi ledger is publication state only — never Bedrock classify / is_add_category.
  const ledgerError =
    ledgerKey === 'ERROR' ? (moderationLastError ?? '').trim() || null : null;
  const showLedgerError = !!ledgerError;
  const deepScanDuration =
    intelligence?.deep_scan_duration_ms != null
      ? formatDurationMs(intelligence.deep_scan_duration_ms)
      : null;
  const settledDetails = () =>
    formatCoopSettledDetails({
      decidedAt: moderationDecidedAt,
      deepScanDuration,
      caseStatus: intelligence?.case_status,
      nowMs,
    });

  let scanLabel = 'Coop · Pending';
  let scanSecondary: string | null = null;
  let scanPhase: string | null = null;
  let scanDetails: CoopScanDetail[] = [];
  let scanHint = 'Media-manager mirror (when delivered)';
  if (isLoading) {
    scanLabel = '';
  } else if (awaitingDelivery) {
    // Real outbox stub only (HOLD/LIVE_ENFORCE). Do not invent pending from OBSERVE ledger.
    const coopStatus = formatCoopScanStatus({
      deliveryStatus: intelligence?.delivery_status ?? null,
      deliveryLastError: intelligence?.delivery_last_error,
      deliveryLastStage: intelligence?.delivery_last_stage,
      deliveryAttemptCount: intelligence?.delivery_attempt_count,
      deliveryLastAttemptAt: intelligence?.delivery_last_attempt_at,
      deliveryNextAttemptAt: intelligence?.delivery_next_attempt_at,
      nowMs,
      elapsed,
    });
    scanLabel = coopStatus.label;
    scanPhase = coopStatus.phase;
    scanDetails = coopStatus.details;
    scanHint = coopStatus.hint;
    scanSecondary =
      coopStatus.details.length > 0
        ? null
        : elapsed
          ? `Case not in media-manager yet · ${elapsed}`
          : 'Case not in media-manager yet; TipMi is waiting on delivery';
  } else if (missing) {
    scanLabel = 'No Coop case';
    scanSecondary = ledgerPendingMissing
      ? 'TipMi ledger is PENDING (observe / no Coop case on create)'
      : ledgerKey === 'ERROR'
        ? 'Case was never opened or is unavailable'
        : 'Never opened or unavailable for this content';
  } else {
    const deliveryLabel = formatDeliveryLabel(intelligence?.delivery_status);
    const delivery = normalizeDeliveryStatus(intelligence?.delivery_status);
    if (delivery === 'failed' && !intelligence?.verdict) {
      const failedStatus = formatCoopScanStatus({
        deliveryStatus: 'failed',
        deliveryLastError: intelligence?.delivery_last_error ?? intelligence?.verdict_reason,
        deliveryLastStage: intelligence?.delivery_last_stage,
        deliveryAttemptCount: intelligence?.delivery_attempt_count,
        deliveryLastAttemptAt: intelligence?.delivery_last_attempt_at,
        nowMs,
      });
      scanLabel = deliveryLabel ?? failedStatus.label;
      scanPhase = failedStatus.phase;
      scanDetails = failedStatus.details;
      scanHint = failedStatus.hint;
      scanSecondary =
        failedStatus.details.length > 0 ? null : 'Case delivery to media-manager failed';
    } else if (provisional) {
      // Do not echo weak mirror statuses (e.g. "completed") — they contradict Scanning.
      const scanningStatus = formatCoopScanStatus({
        deliveryStatus: intelligence?.delivery_status ?? 'delivered',
        deliveryLastError: intelligence?.delivery_last_error,
        deliveryLastStage: intelligence?.delivery_last_stage,
        deliveryAttemptCount: intelligence?.delivery_attempt_count,
        deliveryLastAttemptAt: intelligence?.delivery_last_attempt_at,
        deliveryNextAttemptAt: intelligence?.delivery_next_attempt_at,
        nowMs,
        elapsed,
        scanning: true,
      });
      scanLabel = scanningStatus.label;
      scanPhase = scanningStatus.phase;
      scanHint = scanningStatus.hint;
      scanDetails = scanningStatus.details;
    } else {
      scanLabel = formatCoopScanBadge({
        caseStatus: intelligence?.case_status,
        deepScanSettled,
        enforcementActions: intelligence?.enforcement_actions,
        enforcementPreviews: intelligence?.enforcement_previews,
        verdict: intelligence?.verdict,
      });
      scanHint = 'Live Coop case / enforcement (not TipMi rule preview)';
      const caseStatus = (intelligence?.case_status ?? '').toLowerCase();
      if (caseStatus === 'failed' || caseStatus === 'timeout') {
        scanDetails = formatCoopSettledDetails({
          decidedAt: moderationDecidedAt,
          deepScanDuration,
          caseStatus: intelligence?.case_status,
          verdictReason:
            intelligence?.verdict_reason?.trim() ||
            (caseStatus === 'timeout' ? 'Scan timed out' : 'Scan failed'),
          nowMs,
        });
      } else {
        scanDetails = settledDetails();
      }
    }
  }

  const openInCoopHref =
    intelligence != null && !awaitingDelivery
      ? coopInvestigationUrl(intelligence.case_id, intelligence.coop_url)
      : null;

  const rejectAttribution = rejectAttributionForScanLabel(scanLabel, intelligence);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm">Moderation overview</CardTitle>
        {openInCoopHref ? (
          <a
            href={openInCoopHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            Open in Coop
            <ExternalLink className="size-3" />
          </a>
        ) : null}
      </CardHeader>
      <CardContent className="p-0 pt-0">
        {rejectAttribution ? (
          <p
            className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-900 break-words"
            data-testid="reject-attribution-banner"
          >
            {formatRejectBanner(rejectAttribution)}
          </p>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-3">
          <StatusCell label="TipMi visibility" hint="Feed / hold state">
            {postStatus ? (
              <span className="inline-flex flex-col items-start gap-0.5">
                <Badge variant={postStatusBadgeVariant(postStatus)}>
                  {postStatusLabel(postStatus)}
                </Badge>
                {publicationHint ? (
                  <span className="text-xs text-muted-foreground">{publicationHint}</span>
                ) : null}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">N/A</span>
            )}
          </StatusCell>

          <StatusCell label="TipMi ledger" hint="Publication ledger">
            <span className="inline-flex flex-col items-start gap-0.5">
              <Badge variant={moderationStatusBadgeVariant(status)}>
                {moderationStatusLabel(status)}
              </Badge>
              {elapsed ? (
                <span className="text-xs text-muted-foreground">Pending · {elapsed}</span>
              ) : null}
              {duration ? (
                <span className="text-xs text-muted-foreground">Took {duration}</span>
              ) : null}
              {ledgerError ? (
                <span className="text-xs text-destructive break-words" title={ledgerError}>
                  {ledgerError}
                </span>
              ) : null}
            </span>
          </StatusCell>

          <StatusCell label="Coop scan" hint={scanHint}>
            {isLoading ? (
              <Skeleton className="h-6 w-28 rounded-md" />
            ) : (
              <div className="flex min-w-0 flex-col items-start gap-1.5">
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs',
                    scanBadgeClass(
                      intelligence,
                      provisional,
                      missing,
                      awaitingDelivery,
                    ),
                  )}
                >
                  {scanLabel}
                </Badge>
                <CoopScanDetails details={scanDetails} className="w-full" />
                {scanSecondary ? (
                  <span className="text-xs text-muted-foreground break-words">{scanSecondary}</span>
                ) : null}
              </div>
            )}
          </StatusCell>
        </div>

        {hidePending ? (
          <p className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            TipMi rule preview says Rejected, but TipMi is still not hidden (Waiting / Published). Hide
            has not been applied yet.
          </p>
        ) : null}
        {holdPending ? (
          <p className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            TipMi rule preview says Escalated, but TipMi is still Published. Hold (Waiting) has not
            been applied yet — this is not a live Coop action.
          </p>
        ) : null}
        {publishPending ? (
          <p className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            TipMi rule preview says Approved, but TipMi is still held (Waiting — not published).
            Publish has not been applied yet.
          </p>
        ) : null}
        {awaitingDelivery ? (
          <p className="border-t border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-900">
            {scanPhase === 'scanning'
              ? 'Coop · Scanning (IN FLIGHT) — media-manager deep scan is still running. Watch Status, Stage, Next poll, and Last activity above; if those keep updating, the job is not hung. This page refreshes automatically.'
              : scanPhase === 'retrying_now'
                ? 'Coop delivery is retrying against media-manager now. This page refreshes automatically.'
                : scanPhase === 'retry_waiting'
                  ? 'Coop delivery is delayed and will retry automatically. Last and next retry times are shown above.'
                  : 'Coop case is queued for delivery to media-manager. This page refreshes automatically.'}
          </p>
        ) : null}
        {provisional && !missing && !awaitingDelivery ? (
          <p className="border-t border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-900">
            Coop · Scanning (IN FLIGHT) — media-manager AI scan is still running. No final Coop verdict yet. Watch Status and Last activity above; if they stall for a long time, the job may be stuck.
          </p>
        ) : null}
        {showLedgerError && ledgerError ? (
          <p className="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-900 break-words">
            TipMi ledger error: {ledgerError}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
